import React from 'react';

import Icon, {Icons} from '@pb/kit-component-icon';
import {ListBoxItem} from '@pb/kit-component-listbox';
import {MenuItemCaption, MenuItemMeta} from '@pb/kit-component-menu';
import {withCssModule} from '@pb/kit-util-with-css-module';

import comboBoxStyles from './combo-box.component.module.styl';

import type {ListBoxItemProps} from '@pb/kit-component-listbox';


const classNames = withCssModule(comboBoxStyles);

type _ListBoxItemProps = Omit<ListBoxItemProps, 'caption' | 'value'>;

export interface ComboboxItemProps<ItemValue> extends _ListBoxItemProps {
    /**
     * Plain-text label shown inside the listbox row and used as the selected
     * value displayed in the `ComboBoxInput`. Must be a string (not JSX) because
     * the component reads it to build the comma-separated caption string.
     */
    caption: string,

    /**
     * Internal flag injected by `<ComboBox>` via `Children.map`. Do not set this
     * prop manually — it controls whether the checkmark icon is suppressed when
     * the parent is in dropdown (non-check) mode.
     */
    dropdown?: boolean,

    /**
     * The opaque value associated with this item. Passed back to `ComboBox.onChange`
     * when the item is selected. Can be any type — string, number, or object.
     */
    value: ItemValue,
}


/**
 * Selectable row inside a `<ComboBox>` listbox. Renders `caption`, an optional
 * `meta` node, and a checkmark icon that appears when the item is selected.
 *
 * Pass custom `children` to completely override the default layout (caption +
 * meta + check icon). The `value` and `dropdown` props are consumed by
 * `<ComboBox>` internally and are not forwarded to the DOM.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-combo-box}
 */
function ComboboxItem<ItemValue>({
    dropdown,
    caption,
    meta,
    children,
    // to filter restProps, used in Combobox via Children.map
    value,
    ...props
}: ComboboxItemProps<ItemValue>) {
    return (
        <ListBoxItem {...props}>
            {children ?? (
                <>
                    <MenuItemCaption>
                        {caption}
                    </MenuItemCaption>
                    {meta ? (
                        <MenuItemMeta>
                            {meta}
                        </MenuItemMeta>
                    ) : null}
                    {!dropdown ? (
                        <Icon
                            type={Icons.CHECK}
                            className={classNames('item__check', {selected: props.selected})}
                        />
                    ) : null}
                </>
            )}
        </ListBoxItem>
    );
}

ComboboxItem.displayName = 'ComboboxItem';

export {ComboboxItem};
